import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { canBuyLeads } from '@/lib/roles';
import type { Listing } from '@/components/JobCard';

/**
 * SAVED LEADS — single source of truth (table: mkt_saved_leads).
 *
 * BUSINESS RULES (enforced by design, not by UI):
 *  - Saving NEVER unlocks, purchases or reserves a lead. The table is fully
 *    separate from mkt_lead_purchases / mkt_credit_ledger, and no purchase or
 *    capacity code reads it.
 *  - RLS: a user can only read / insert / delete their OWN rows (admins read).
 *  - UNIQUE (user_id, listing_id) makes duplicate saves impossible; inserts use
 *    ignore-duplicates so a double tap is a no-op, not an error.
 *  - ON DELETE CASCADE removes saves when a listing is deleted; unpublished or
 *    closed listings come back from mkt_my_saved_leads() with available=false.
 *
 * PERFORMANCE: one tiny `listing_id` read per session, hydrated instantly from
 * localStorage, shared by every card through an external store (no context
 * re-render of the feed). Toggles are optimistic and revert on failure.
 */

type State = { userId: string | null; ids: Set<string>; loaded: boolean; version: number };

let state: State = { userId: null, ids: new Set(), loaded: false, version: 0 };
const listeners = new Set<() => void>();
const pending = new Set<string>();
let inflight: Promise<void> | null = null;

const cacheKey = (uid: string) => `gg_saved_leads_v1:${uid}`;

const emit = () => {
  state = { ...state, version: state.version + 1 };
  listeners.forEach((l) => l());
};

const persist = () => {
  if (!state.userId) return;
  try {
    localStorage.setItem(cacheKey(state.userId), JSON.stringify([...state.ids]));
  } catch {
    /* storage full / private mode — server stays authoritative */
  }
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;

/** Hydrate from cache immediately, then reconcile with the server once. */
export const ensureSavedLoaded = (userId: string | null | undefined) => {
  if (!userId) {
    if (state.userId) {
      state = { userId: null, ids: new Set(), loaded: false, version: state.version };
      emit();
    }
    return;
  }
  if (state.userId === userId && (state.loaded || inflight)) return;

  let cached: string[] = [];
  try {
    cached = JSON.parse(localStorage.getItem(cacheKey(userId)) || '[]');
  } catch {
    cached = [];
  }
  state = { userId, ids: new Set(Array.isArray(cached) ? cached : []), loaded: false, version: state.version };
  emit();

  inflight = (async () => {
    const { data, error } = await supabase
      .from('mkt_saved_leads')
      .select('listing_id')
      .eq('user_id', userId)
      .limit(1000);
    if (state.userId !== userId) return;
    if (!error && Array.isArray(data)) {
      const server = new Set<string>(data.map((r: { listing_id: string }) => r.listing_id));
      // Keep any toggle the user made while the read was in flight.
      pending.forEach((id) => (state.ids.has(id) ? server.add(id) : server.delete(id)));
      state = { ...state, ids: server, loaded: true };
      persist();
    } else {
      state = { ...state, loaded: true };
    }
    emit();
  })().finally(() => {
    inflight = null;
  });
};

/** Optimistic toggle. Resolves to the final saved state, or throws on failure (already reverted). */
export const toggleSavedLead = async (userId: string, listingId: string): Promise<boolean> => {
  if (pending.has(listingId)) return state.ids.has(listingId);
  const wasSaved = state.ids.has(listingId);
  const next = new Set(state.ids);
  if (wasSaved) next.delete(listingId);
  else next.add(listingId);
  state = { ...state, ids: next };
  pending.add(listingId);
  persist();
  emit();

  try {
    const { error } = wasSaved
      ? await supabase.from('mkt_saved_leads').delete().eq('user_id', userId).eq('listing_id', listingId)
      : await supabase
          .from('mkt_saved_leads')
          .upsert({ user_id: userId, listing_id: listingId }, { onConflict: 'user_id,listing_id', ignoreDuplicates: true });
    if (error) throw error;
    return !wasSaved;
  } catch (e) {
    const revert = new Set(state.ids);
    if (wasSaved) revert.add(listingId);
    else revert.delete(listingId);
    state = { ...state, ids: revert };
    persist();
    emit();
    throw e;
  } finally {
    pending.delete(listingId);
  }
};

/** Hook used by every save button + the dashboard. Cheap: one shared store. */
export const useSavedLeads = () => {
  const { profile } = useAuth();
  const userId = profile?.id || null;
  const enabled = !profile || canBuyLeads(profile.user_type);
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (enabled) ensureSavedLoaded(userId);
  }, [userId, enabled]);

  const isSaved = useCallback((id: string) => snap.userId === userId && snap.ids.has(id), [snap, userId]);
  const toggle = useCallback(
    (id: string) => (userId ? toggleSavedLead(userId, id) : Promise.reject(new Error('auth_required'))),
    [userId],
  );

  return { userId, enabled, isSaved, toggle, count: snap.userId === userId ? snap.ids.size : 0, loaded: snap.loaded };
};

// ─────────────────────────────── dashboard ────────────────────────────────

export interface SavedLeadRow {
  listing_id: string;
  saved_at: string;
  available: boolean;
  purchased: boolean;
  listing: Listing | null;
}

/** Caller's saved leads with public-safe listing fields (SECURITY DEFINER RPC, own rows only). */
export const fetchMySavedLeads = async (limit = 200): Promise<SavedLeadRow[]> => {
  const { data, error } = await supabase.rpc('mkt_my_saved_leads', { p_limit: limit });
  if (error) throw error;
  return Array.isArray(data) ? (data as SavedLeadRow[]) : [];
};

// ──────────────────────────────── admin ───────────────────────────────────

export interface SavedLeadsReport {
  total_saves: number;
  saved_listings: number;
  savers: number;
  saves_7d: number;
  top_listings: Array<{
    listing_id: string; saves: number; last_saved_at: string; title: string; trade: string; city: string | null;
    state: string | null; slug: string; status: string; contractor_count: number; max_contractors: number; purchases: number;
  }>;
  top_users: Array<{ user_id: string; saves: number; last_saved_at: string; email: string | null; company_name: string | null; user_type: string | null }>;
}

export const fetchSavedLeadsReport = async (limit = 25): Promise<SavedLeadsReport> => {
  const { data, error } = await supabase.rpc('mkt_admin_saved_leads_report', { p_limit: limit });
  if (error) throw error;
  return data as SavedLeadsReport;
};
